import type { SavedPost } from '@/types/gallery';

interface SceneGroup {
  id: string;
  name: string;
  description: string;
  icon: string;
  scenes: Scene[];
}

// AestheticId — one per visual "vibe" group. Drives StepBackground's tab
// row. Adding a new aesthetic means: extend this union, add a SceneGroup
// entry below, and tag relevant templates with the new value.
//
// Note: 'luxury' was previously 'coastal-med' — renamed for cross-product
// generality (luxury car interiors, luxury watches, etc. would fit even
// though the v1 templates are all coastal/Mediterranean). The id changed
// in scene entries too; no consumer outside this file pins to the id.
export type AestheticId =
  | 'luxury'
  | 'spa-wellness'
  | 'soft-lifestyle'
  | 'nature'
  | 'moody-dramatic';

interface Scene {
  id: string;
  name: string;
  description: string;
  image: string;
  resultPrefix: string;
  /** Which aesthetic tab this template lives under. One value per scene —
   *  templates don't span multiple groups (would make the picker confusing
   *  and complicates the "Recommended for you" math). */
  aesthetic: AestheticId;
  /** Free-form descriptor + product-fit tags. Recommendation step in
   *  StepBackground scores templates by overlap between this list and
   *  the tags analyze-product returns for the uploaded product. Keep
   *  4–8 per template — too few and recommendations are noisy, too
   *  many and every product matches every template. */
  tags: string[];
}

// Scene IDs map 1:1 to backend template IDs (template1..templateN).
// Thumbnails are served from frontend/public/templates/<id>/frame1_thumb.png.
// When adding new templates:
//   1. drop folders in frontend/public/templates/<id>/ + backend/templates/<id>/
//   2. append a Scene entry to the appropriate sceneGroup below with
//      `aesthetic` + `tags` populated (NOT the legacy `Template N` style —
//      every new scene should be hand-tagged so recommendations work).
//   3. that's it. No central counter to bump anymore.
//
// Scene names + tags were assigned by visual inspection of each thumbnail
// on May 17, 2026. See memory/wizard_picker_grid.md for the convention.
export const sceneGroups: SceneGroup[] = [
  {
    id: 'luxury',
    name: 'Luxury',
    description: 'Greek villa, marble, poolside, sea views, yacht, terracotta',
    icon: 'ri-vip-crown-line',
    scenes: [
      {
        id: 'template3',
        name: 'Terracotta Summer',
        description: 'Warm terracotta wall with palm-frond shadow',
        image: '/templates/template3/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['warm-light', 'terracotta', 'summer', 'fragrance', 'beauty', 'vacation'],
      },
      {
        id: 'template9',
        name: 'Mediterranean Arch',
        description: 'Marble surface with arched view of mountains and water',
        image: '/templates/template9/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['marble', 'sea', 'arch', 'luxury', 'premium', 'vacation', 'fragrance', 'refined', 'modern'],
      },
      {
        id: 'template10',
        name: 'Seaside Terrace',
        description: 'Sheer curtains, ocean view, travertine ledge',
        image: '/templates/template10/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['travertine', 'sea', 'soft-light', 'spa', 'wellness', 'feminine', 'luxury'],
      },
      {
        id: 'template12',
        name: 'Desert Oasis',
        description: 'Adobe-walled poolside with olive trees',
        image: '/templates/template12/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['warm-light', 'stone', 'olive', 'masculine', 'fragrance', 'vacation', 'spa', 'refined', 'leather'],
      },
      {
        id: 'template13',
        name: 'Cliffside Villa',
        description: 'Mediterranean cliffside pool with stone columns',
        image: '/templates/template13/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['sea', 'travertine', 'columns', 'luxury', 'vacation', 'premium', 'fragrance'],
      },
      {
        id: 'template14',
        name: 'Beach Villa',
        description: 'Modern luxury beachfront with pool and palms',
        image: '/templates/template14/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['sea', 'travertine', 'modern', 'luxury', 'vacation', 'spa', 'fragrance', 'refined'],
      },
      {
        id: 'template17',
        name: 'Santorini Arch',
        description: 'Whitewashed Greek arch with sea view and olive branch',
        image: '/templates/template17/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['sea', 'arch', 'olive', 'travertine', 'luxury', 'vacation', 'fragrance'],
      },
      {
        id: 'template18',
        name: 'Coastal Bluff',
        description: 'Open Mediterranean cliff with olive and white podium',
        image: '/templates/template18/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['sea', 'travertine', 'olive', 'fresh', 'wellness', 'fragrance'],
      },
      {
        id: 'template21',
        name: 'Yacht Lounge',
        description: 'Luxury yacht interior with leather seating and ocean view',
        image: '/templates/template21/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['yacht', 'leather', 'sea', 'luxury', 'premium', 'masculine', 'travel', 'refined', 'modern'],
      },
      {
        id: 'template30',
        name: 'Skyline Terrace',
        description: 'Rooftop terrace at golden hour overlooking a hazy city skyline',
        image: '/templates/template30/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['luxury', 'premium', 'warm-light', 'urban', 'refined', 'modern', 'stone'],
      },
      {
        id: 'template31',
        name: 'Skyline Terrace Glow',
        description: 'Sunset rooftop terrace with glass railings and a city panorama',
        image: '/templates/template31/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['luxury', 'premium', 'warm-light', 'urban', 'refined', 'modern', 'stone'],
      },
      {
        id: 'template32',
        name: 'Mahogany Boutique',
        description: 'Warm mahogany boutique interior with brass accents and soft uplight',
        image: '/templates/template32/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['luxury', 'premium', 'warm-light', 'wood', 'refined', 'gold', 'fragrance'],
      },
      {
        id: 'template35',
        name: 'Walnut Niche Glow',
        description: 'Warm walnut shelves and pedestal under a soft directional spotlight',
        image: '/templates/template35/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['warm-light', 'wood', 'refined', 'premium', 'intimate', 'luxury', 'minimal'],
      },
      {
        id: 'template45',
        name: 'Gilt Mirror Vanity',
        description: 'Vintage gilt mirror above a marble vanity with brass tray',
        image: '/templates/template45/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['luxury', 'premium', 'gold', 'marble', 'refined', 'warm-light', 'feminine'],
      },
      {
        id: 'template51',
        name: 'Poolside Cabana',
        description: 'Sunlit poolside tile with a striped beach towel',
        image: '/templates/template51/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['vacation', 'summer', 'luxury', 'warm-light', 'fresh', 'beach', 'premium'],
      },
      {
        id: 'template56',
        name: 'Walnut Wardrobe',
        description: 'Warm wooden walk-in closet with suede bench and brass pulls',
        image: '/templates/template56/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['luxury', 'premium', 'wood', 'warm-light', 'refined', 'intimate', 'leather'],
      },
      {
        id: 'template58',
        name: 'Brass Tap Marble',
        description: 'Calacatta marble counter with vintage brass faucet',
        image: '/templates/template58/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['marble', 'gold', 'luxury', 'premium', 'refined', 'spa', 'skincare'],
      },
      {
        id: 'template70',
        name: 'Walnut Display Niche',
        description: 'Empty walnut shelving alcove lit by warm hidden strips',
        image: '/templates/template70/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['wood', 'warm-light', 'refined', 'premium', 'luxury', 'intimate', 'minimal'],
      },
      {
        id: 'template74',
        name: 'Champagne Diamond Stage',
        description: 'Champagne satin drapery flanked by glittering diamond facets',
        image: '/templates/template74/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['luxury', 'premium', 'gold', 'refined', 'feminine', 'dreamy', 'fragrance'],
      },
      {
        id: 'template79',
        name: 'Pearl Vanity Glow',
        description: 'Marble vanity with gold mirror, perfume and pearl jewelry',
        image: '/templates/template79/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['luxury', 'premium', 'marble', 'gold', 'feminine', 'refined', 'beauty'],
      },
      {
        id: 'template91',
        name: 'Onyx Gold Frame Stage',
        description: 'Black stage framed by thin gold trim and a single overhead beam',
        image: '/templates/template91/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['luxury', 'premium', 'gold', 'moody', 'dramatic', 'refined', 'fragrance'],
      },
      {
        id: 'template92',
        name: 'Ruby Velvet Frame',
        description: 'Yellow gold frame floating against rich ruby red velvet drapes',
        image: '/templates/template92/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['luxury', 'premium', 'gold', 'dramatic', 'intimate', 'refined', 'feminine'],
      },
      {
        id: 'template93',
        name: 'Bokeh Cabinet Niche',
        description: 'Warm walnut cabinet niche framed by golden bokeh lights',
        image: '/templates/template93/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'luxury',
        tags: ['warm-light', 'wood', 'luxury', 'premium', 'intimate', 'refined', 'fragrance'],
      },
    ],
  },
  {
    id: 'spa-wellness',
    name: 'Spa & Wellness',
    description: 'Bathrooms, travertine, dried botanicals, soft warm light',
    icon: 'ri-drop-line',
    scenes: [
      {
        id: 'template15',
        name: 'Wood Spa',
        description: 'Warm wood bathroom with stone basin and plants',
        image: '/templates/template15/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['wood', 'spa', 'wellness', 'warm-light', 'natural', 'skincare', 'masculine'],
      },
      {
        id: 'template16',
        name: 'Travertine Vanity',
        description: 'Travertine ledge with eucalyptus in ceramic vase',
        image: '/templates/template16/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['travertine', 'plants', 'soft-light', 'spa', 'skincare', 'beauty', 'wellness'],
      },
      {
        id: 'template19',
        name: 'Gold & Marble Bath',
        description: 'Luxury marble bathroom with gold fixtures',
        image: '/templates/template19/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['marble', 'gold', 'luxury', 'premium', 'beauty', 'skincare', 'feminine'],
      },
      {
        id: 'template20',
        name: 'Travertine Spa',
        description: 'Travertine spa room with folded towels and dried flowers',
        image: '/templates/template20/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['travertine', 'spa', 'wellness', 'warm-light', 'skincare', 'feminine', 'fragrance'],
      },
      {
        id: 'template22',
        name: 'Gold Vanity',
        description: 'Warm travertine vanity with brass fixtures and mirror',
        image: '/templates/template22/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['travertine', 'gold', 'warm-light', 'beauty', 'skincare', 'feminine', 'luxury'],
      },
      {
        id: 'template36',
        name: 'Rose Marble Vanity',
        description: 'Blush pink marble vanity with rose gold tray and mirror',
        image: '/templates/template36/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['marble', 'feminine', 'gold', 'beauty', 'skincare', 'soft-light', 'refined'],
      },
      {
        id: 'template38',
        name: 'Travertine Niche',
        description: 'Warm travertine wall niche softened by trailing eucalyptus',
        image: '/templates/template38/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['travertine', 'stone', 'spa', 'wellness', 'soft-light', 'organic', 'natural'],
      },
      {
        id: 'template42',
        name: 'Linen Yoga Corner',
        description: 'Calm beige yoga corner with jute mat and potted palm',
        image: '/templates/template42/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['wellness', 'natural', 'soft-light', 'organic', 'neutral', 'minimal', 'spa'],
      },
      {
        id: 'template55',
        name: 'Apothecary Bench',
        description: 'Wood bench with amber bottles and dried botanical bundles',
        image: '/templates/template55/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['spa', 'wellness', 'organic', 'wood', 'warm-light', 'fragrance', 'natural'],
      },
      {
        id: 'template57',
        name: 'Crisp White Vanity',
        description: 'Bright white tiled vanity with brushes and cotton pads',
        image: '/templates/template57/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['beauty', 'skincare', 'fresh', 'minimal', 'soft-light', 'neutral', 'wellness'],
      },
      {
        id: 'template67',
        name: 'Beige Stone Counter',
        description: 'Warm beige stone counter with folded towels and eucalyptus',
        image: '/templates/template67/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['spa', 'wellness', 'travertine', 'stone', 'neutral', 'soft-light', 'skincare'],
      },
      {
        id: 'template77',
        name: 'Bright Kitchen Counter',
        description: 'Warmly lit white kitchen counter with a fruit bowl',
        image: '/templates/template77/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['fresh', 'warm-light', 'natural', 'wellness', 'neutral', 'minimal', 'wood'],
      },
      {
        id: 'template80',
        name: 'Artisan Studio Bench',
        description: 'Painter\'s palette and brushes on a warm beige plaster shelf',
        image: '/templates/template80/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['organic', 'natural', 'warm-light', 'neutral', 'wood', 'refined', 'minimal'],
      },
      {
        id: 'template83',
        name: 'Turmeric Wall Spice',
        description: 'Ochre plaster wall with turmeric powder and ginger root',
        image: '/templates/template83/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['organic', 'warm-light', 'natural', 'wellness', 'spa', 'moody', 'intimate'],
      },
      {
        id: 'template84',
        name: 'Sage Bathhouse Bench',
        description: 'Sage tiled sauna bench under a single warm spotlight',
        image: '/templates/template84/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['spa', 'wellness', 'wood', 'warm-light', 'minimal', 'intimate', 'stone'],
      },
      {
        id: 'template86',
        name: 'Peach Dew Drops',
        description: 'Peach glass surface covered in fresh water droplets',
        image: '/templates/template86/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['fresh', 'skincare', 'beauty', 'feminine', 'soft-light', 'dreamy', 'wellness'],
      },
      {
        id: 'template87',
        name: 'Aqua Tile Counter',
        description: 'Aqua blue tiled wall with a white stone ledge',
        image: '/templates/template87/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'spa-wellness',
        tags: ['fresh', 'spa', 'wellness', 'soft-light', 'stone', 'skincare', 'beauty'],
      },
    ],
  },
  {
    id: 'soft-lifestyle',
    name: 'Soft & Lifestyle',
    description: 'Bedroom, pastels, dreamy, feminine, fresh',
    icon: 'ri-heart-line',
    scenes: [
      {
        id: 'template2',
        name: 'Eucalyptus Sky',
        description: 'Pastel blue background with eucalyptus and white podium',
        image: '/templates/template2/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['soft-light', 'plants', 'fresh', 'beauty', 'skincare', 'feminine', 'wellness'],
      },
      {
        id: 'template7',
        name: 'Cloudscape Dream',
        description: 'Sky-blue background with white clouds and pink podium',
        image: '/templates/template7/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['dreamy', 'fresh', 'feminine', 'beauty', 'fragrance', 'soft-light'],
      },
      {
        id: 'template11',
        name: 'Boudoir Suite',
        description: 'Cream bedroom with soft linens and marble podium',
        image: '/templates/template11/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['bedroom', 'soft-light', 'feminine', 'beauty', 'skincare', 'fragrance', 'intimate'],
      },
      {
        id: 'template34',
        name: 'Sage Dance Studio',
        description: 'Sunlit dance studio with light oak floors and sage paneled walls',
        image: '/templates/template34/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['soft-light', 'warm-light', 'wood', 'wellness', 'natural', 'feminine', 'minimal'],
      },
      {
        id: 'template39',
        name: 'Cream Linen Sweep',
        description: 'Soft cream studio sweep with delicate shadow play',
        image: '/templates/template39/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['soft-light', 'minimal', 'neutral', 'feminine', 'studio', 'dreamy', 'beauty'],
      },
      {
        id: 'template44',
        name: 'Blush Glass Block',
        description: 'Playful pastel pink studio with glass block accent wall',
        image: '/templates/template44/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['feminine', 'soft-light', 'dreamy', 'minimal', 'beauty', 'modern', 'intimate'],
      },
      {
        id: 'template46',
        name: 'Honeyed Bedroom Light',
        description: 'Sunlit bedroom corner with linen bedding and a wooden chair',
        image: '/templates/template46/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['warm-light', 'soft-light', 'intimate', 'wood', 'dreamy', 'feminine', 'neutral'],
      },
      {
        id: 'template60',
        name: 'Morning Gym Bag',
        description: 'Bright morning desk with a gym bag and water bottle',
        image: '/templates/template60/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['athletic', 'fresh', 'soft-light', 'wellness', 'minimal', 'natural', 'modern'],
      },
      {
        id: 'template63',
        name: 'Coral Brushstroke',
        description: 'Acrylic risers on peach with bold coral brushstrokes',
        image: '/templates/template63/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['feminine', 'dreamy', 'soft-light', 'beauty', 'modern', 'minimal', 'intimate'],
      },
      {
        id: 'template64',
        name: 'Petal Silk Floor',
        description: 'Blush silk drapery with scattered rose petals',
        image: '/templates/template64/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['feminine', 'dreamy', 'intimate', 'soft-light', 'beauty', 'fragrance', 'refined'],
      },
      {
        id: 'template68',
        name: 'Lavender Mist Studio',
        description: 'Soft pastel lavender studio corner with gentle gradient',
        image: '/templates/template68/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['dreamy', 'soft-light', 'feminine', 'minimal', 'beauty', 'neutral', 'fragrance'],
      },
      {
        id: 'template72',
        name: 'Mint Glass Cube',
        description: 'White cube on muted mint floor with translucent green glass panels',
        image: '/templates/template72/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['fresh', 'minimal', 'modern', 'soft-light', 'dreamy', 'neutral', 'beauty'],
      },
      {
        id: 'template75',
        name: 'Pastel Color Block',
        description: 'Bold pastel yellow, blue and pink corner with crisp sunlight',
        image: '/templates/template75/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['dreamy', 'fresh', 'feminine', 'soft-light', 'modern', 'minimal', 'beauty'],
      },
      {
        id: 'template95',
        name: 'Linen Reading Nook',
        description: 'Soft beige linen surface with an open book and water glass',
        image: '/templates/template95/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'soft-lifestyle',
        tags: ['soft-light', 'intimate', 'neutral', 'minimal', 'dreamy', 'feminine', 'wellness'],
      },
    ],
  },
  {
    id: 'nature',
    name: 'Nature & Outdoors',
    description: 'Beach, forest, meadow, organic settings',
    icon: 'ri-plant-line',
    scenes: [
      {
        id: 'template5',
        name: 'Golden Beach',
        description: 'Beach sand and ocean at sunset',
        image: '/templates/template5/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['beach', 'golden-hour', 'sea', 'sand', 'vacation', 'summer', 'beverage', 'fragrance'],
      },
      {
        id: 'template6',
        name: 'Mossy Woodland',
        description: 'Dark forest floor with mossy ground and twisted branches',
        image: '/templates/template6/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['forest', 'plants', 'moody', 'organic', 'wellness', 'masculine', 'skincare', 'outdoor', 'athletic'],
      },
      {
        id: 'template8',
        name: 'Meadow Bloom',
        description: 'Green mossy hills with tiny white wildflowers',
        image: '/templates/template8/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['outdoor', 'plants', 'fresh', 'wellness', 'skincare', 'fragrance', 'feminine', 'athletic', 'natural'],
      },
      {
        id: 'template41',
        name: 'Citrus Subway Counter',
        description: 'Bright kitchen counter with subway tile and fresh citrus',
        image: '/templates/template41/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['fresh', 'natural', 'organic', 'soft-light', 'minimal', 'wellness', 'beauty'],
      },
      {
        id: 'template43',
        name: 'Open Road Sunset',
        description: 'Empty highway stretching toward a vivid orange sunset',
        image: '/templates/template43/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['outdoor', 'warm-light', 'summer', 'vacation', 'dramatic', 'natural', 'masculine'],
      },
      {
        id: 'template49',
        name: 'Track Sunrise Podium',
        description: 'Running track at sunrise with a low podium catching warm light',
        image: '/templates/template49/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['athletic', 'outdoor', 'warm-light', 'summer', 'natural', 'dramatic', 'masculine'],
      },
      {
        id: 'template54',
        name: 'Forest Trail',
        description: 'Mossy rock beside a sunlit forest path in summer',
        image: '/templates/template54/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['forest', 'outdoor', 'natural', 'organic', 'fresh', 'summer', 'soft-light'],
      },
      {
        id: 'template66',
        name: 'Coastal Window Shelf',
        description: 'White shiplap shelf beside a breezy seaside window',
        image: '/templates/template66/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['beach', 'sea', 'fresh', 'soft-light', 'wood', 'summer', 'vacation'],
      },
      {
        id: 'template73',
        name: 'Palm Shadow Linen',
        description: 'Cream and terracotta linen with palm frond shadow',
        image: '/templates/template73/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['summer', 'vacation', 'warm-light', 'natural', 'organic', 'beach', 'soft-light'],
      },
      {
        id: 'template78',
        name: 'Mountain Hiking Path',
        description: 'Rocky trail winding through sunlit mountain peaks',
        image: '/templates/template78/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['outdoor', 'athletic', 'natural', 'stone', 'summer', 'fresh', 'masculine'],
      },
      {
        id: 'template82',
        name: 'Wheat Linen Square',
        description: 'Natural linen square on kraft paper with dried wheat',
        image: '/templates/template82/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['organic', 'natural', 'neutral', 'soft-light', 'minimal', 'wellness', 'feminine'],
      },
      {
        id: 'template85',
        name: 'Dust Road Horizon',
        description: 'Dusty desert road with rolling scrub hills under a wide sky',
        image: '/templates/template85/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'nature',
        tags: ['outdoor', 'natural', 'summer', 'vacation', 'masculine', 'leather', 'warm-light'],
      },
    ],
  },
  {
    id: 'moody-dramatic',
    name: 'Moody & Dramatic',
    description: 'Dark, intimate, amber-lit, premium',
    icon: 'ri-moon-line',
    scenes: [
      {
        id: 'template1',
        name: 'Cave Reflection',
        description: 'Dark cave-like stone with single flower on water',
        image: '/templates/template1/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['stone', 'water', 'moody', 'dramatic', 'fragrance', 'masculine', 'premium', 'leather', 'refined', 'edgy'],
      },
      {
        id: 'template4',
        name: 'Amber Glow',
        description: 'Dark amber-lit scene with dried branches',
        image: '/templates/template4/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['warm-light', 'moody', 'intimate', 'fragrance', 'candle', 'premium', 'masculine', 'leather', 'refined'],
      },
      {
        id: 'template28',
        name: 'Concrete Loft Plinth',
        description: 'Industrial concrete plinth in a sunlit loft with raw textured walls',
        image: '/templates/template28/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['concrete', 'urban', 'minimal', 'edgy', 'modern', 'studio', 'stone'],
      },
      {
        id: 'template29',
        name: 'City Lights Promenade',
        description: 'Wet stone promenade at night with a glowing skyline bokeh',
        image: '/templates/template29/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['urban', 'moody', 'dramatic', 'modern', 'edgy', 'stone', 'metal'],
      },
      {
        id: 'template33',
        name: 'Showroom Floor',
        description: 'Polished concrete showroom with crisp ceiling spotlights',
        image: '/templates/template33/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['modern', 'minimal', 'studio', 'urban', 'concrete', 'edgy', 'metal'],
      },
      {
        id: 'template37',
        name: 'Obsidian Marble Plinth',
        description: 'Black marble plinth with gold veining against a smoky dark backdrop',
        image: '/templates/template37/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['marble', 'moody', 'dramatic', 'gold', 'luxury', 'premium', 'intimate'],
      },
      {
        id: 'template40',
        name: 'Slate Studio Ledge',
        description: 'Moody concrete studio with a single shaft of dramatic light',
        image: '/templates/template40/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['moody', 'dramatic', 'concrete', 'studio', 'edgy', 'stone', 'masculine'],
      },
      {
        id: 'template47',
        name: 'Onyx Showcase',
        description: 'Glossy black display cube under a single overhead light bar',
        image: '/templates/template47/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['moody', 'dramatic', 'metal', 'edgy', 'premium', 'modern', 'masculine'],
      },
      {
        id: 'template48',
        name: 'Frosted White Cube',
        description: 'Minimalist white frosted glass cube on a pure white sweep',
        image: '/templates/template48/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['minimal', 'studio', 'modern', 'neutral', 'premium', 'refined', 'beauty'],
      },
      {
        id: 'template50',
        name: 'Carbon Black Stage',
        description: 'Deep navy-black carbon textured backdrop with subtle gradient',
        image: '/templates/template50/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['moody', 'dramatic', 'edgy', 'masculine', 'premium', 'studio', 'metal'],
      },
      {
        id: 'template52',
        name: 'Backstage Vanity',
        description: 'Dramatic backstage vanity mirror framed by warm glowing bulbs',
        image: '/templates/template52/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['moody', 'dramatic', 'warm-light', 'beauty', 'edgy', 'intimate', 'premium'],
      },
      {
        id: 'template53',
        name: 'Amber Spotlight Plinth',
        description: 'Warm amber-lit alcove with a single illuminated pedestal',
        image: '/templates/template53/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['warm-light', 'moody', 'intimate', 'dramatic', 'refined', 'premium', 'fragrance'],
      },
      {
        id: 'template59',
        name: 'Studio Softbox Plinth',
        description: 'Concrete plinth with a tall softbox casting moody studio light',
        image: '/templates/template59/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['studio', 'moody', 'dramatic', 'concrete', 'edgy', 'masculine', 'modern'],
      },
      {
        id: 'template61',
        name: 'Neon Garage',
        description: 'Empty garage washed in magenta and blue neon glow',
        image: '/templates/template61/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['edgy', 'urban', 'moody', 'dramatic', 'athletic', 'modern', 'masculine'],
      },
      {
        id: 'template62',
        name: 'Graffiti Concrete Ledge',
        description: 'Concrete ledge in front of a graffiti tagged wall',
        image: '/templates/template62/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['urban', 'edgy', 'concrete', 'athletic', 'modern', 'masculine', 'stone'],
      },
      {
        id: 'template65',
        name: 'Emerald Tiled Arch',
        description: 'Glossy emerald tiled wall with arched niche and palm shadow',
        image: '/templates/template65/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['moody', 'dramatic', 'stone', 'refined', 'intimate', 'premium', 'modern'],
      },
      {
        id: 'template69',
        name: 'Neon Arcade Plinth',
        description: 'Retro futuristic blue neon arch around a glowing pedestal',
        image: '/templates/template69/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['edgy', 'modern', 'dramatic', 'metal', 'urban', 'masculine', 'moody'],
      },
      {
        id: 'template71',
        name: 'Whiskey Wood Stage',
        description: 'Rich wood pedestal with cowboy boot silhouettes in the background',
        image: '/templates/template71/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['leather', 'wood', 'warm-light', 'masculine', 'edgy', 'moody', 'refined'],
      },
      {
        id: 'template76',
        name: 'Rainy Alley Reflections',
        description: 'Wet cobblestone alley reflecting warm city lights at night',
        image: '/templates/template76/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['urban', 'moody', 'dramatic', 'edgy', 'warm-light', 'intimate', 'masculine'],
      },
      {
        id: 'template81',
        name: 'Crimson Velvet Stage',
        description: 'Deep red velvet drape backdrop with dramatic spotlight',
        image: '/templates/template81/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['dramatic', 'moody', 'intimate', 'premium', 'refined', 'warm-light', 'fragrance'],
      },
      {
        id: 'template88',
        name: 'Black Silk Drape',
        description: 'Deep black silk drapery over a dark studio floor',
        image: '/templates/template88/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['moody', 'dramatic', 'intimate', 'premium', 'refined', 'feminine', 'fragrance'],
      },
      {
        id: 'template89',
        name: 'Midnight Tunnel Glow',
        description: 'Empty road tunnel washed in deep teal blue light',
        image: '/templates/template89/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['moody', 'dramatic', 'edgy', 'urban', 'masculine', 'modern', 'metal'],
      },
      {
        id: 'template90',
        name: 'Black Ledge Spotlight',
        description: 'Minimal black ledge with a soft top-down spotlight',
        image: '/templates/template90/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['moody', 'dramatic', 'minimal', 'premium', 'studio', 'edgy', 'masculine'],
      },
      {
        id: 'template94',
        name: 'Brick Loft Rack',
        description: 'Sunlit brick loft with a metal clothing rack against the wall',
        image: '/templates/template94/frame1_thumb.png',
        resultPrefix: '',
        aesthetic: 'moody-dramatic',
        tags: ['urban', 'leather', 'warm-light', 'edgy', 'modern', 'stone', 'studio'],
      },
    ],
  },
  // The "In Hand" / held group (templates 23–27) was promoted to the
  // dedicated Product Swap route on May 29, 2026 — see productSwapScenes
  // in mocks/app-data.ts. It no longer appears as a Product Shot
  // background aesthetic.
];

// Flat array for backward compatibility (used by callers that don't care
// about grouping — e.g. the sceneNames lookup below).
export const demoScenes = sceneGroups.flatMap((g) => g.scenes);

// All scene names lookup
export const sceneNames: Record<string, string> = {};
demoScenes.forEach((s) => {
  sceneNames[s.id] = s.name;
});

// Result images for the "generic" result path
export const resultImages: Record<string, string> = {
  s1: 'https://readdy.ai/api/search-image?query=Luxury%20lifestyle%20product%20photography%20with%20product%20elegantly%20placed%20on%20a%20marble%20coffee%20table%20next%20to%20a%20latte%20art%20coffee%20cup%2C%20morning%20golden%20sunlight%20through%20window%2C%20cozy%20modern%20apartment%20interior%2C%20warm%20ambient%20lighting%2C%20professional%20editorial%20photography%2C%20aspirational%20lifestyle%20scene%2C%20soft%20shadows%2C%20warm%20color%20tones&width=800&height=800&seq=200&orientation=squarish',
  s2: 'https://readdy.ai/api/search-image?query=Luxury%20beauty%20lifestyle%20product%20photography%20with%20product%20on%20white%20marble%20vanity%20with%20gold%20decorative%20accents%2C%20soft%20diffused%20lighting%2C%20elegant%20editorial%20photography%2C%20sophisticated%20aesthetic%2C%20warm%20ambient%20glow%2C%20luxury%20beauty%20setup%2C%20premium%20feel&width=800&height=800&seq=201&orientation=squarish',
  s3: 'https://readdy.ai/api/search-image?query=Summer%20beach%20lifestyle%20product%20photography%20with%20product%20resting%20on%20soft%20white%20sand%2C%20turquoise%20ocean%20in%20background%2C%20warm%20summer%20golden%20hour%20light%2C%20relaxed%20tropical%20beach%20setting%2C%20vacation%20lifestyle%2C%20soft%20natural%20shadows&width=800&height=800&seq=202&orientation=squarish',
  s4: 'https://readdy.ai/api/search-image?query=Cozy%20rustic%20cabin%20lifestyle%20product%20photography%20with%20product%20on%20rustic%20wooden%20table%2C%20warm%20fireplace%20glow%20in%20background%2C%20hygge%20atmosphere%2C%20knit%20textures%20nearby%2C%20warm%20lifestyle%20photography%2C%20amber%20lighting%2C%20comfortable%20rustic%20setting&width=800&height=800&seq=203&orientation=squarish',
  s5: 'https://readdy.ai/api/search-image?query=Minimalist%20modern%20workspace%20lifestyle%20product%20photography%20with%20product%20on%20clean%20white%20desk%2C%20natural%20daylight%20from%20large%20window%2C%20small%20potted%20plant%20nearby%2C%20Scandinavian%20aesthetic%2C%20bright%20airy%20lifestyle%20photography%2C%20clean%20shadows&width=800&height=800&seq=204&orientation=squarish',
  s6: 'https://readdy.ai/api/search-image?query=Urban%20rooftop%20lifestyle%20product%20photography%20with%20product%20on%20modern%20terrace%2C%20city%20skyline%20at%20twilight%20in%20background%2C%20string%20lights%20creating%20ambiance%2C%20deep%20blue%20sky%2C%20sophisticated%20urban%20lifestyle%20photography%2C%20premium%20aesthetic%2C%20soft%20evening%20light&width=800&height=800&seq=205&orientation=squarish',
  s7: 'https://readdy.ai/api/search-image?query=Luxury%20lifestyle%20product%20photography%20with%20product%20on%20a%20white%20marble%20nightstand%20next%20to%20a%20warm%20bedside%20lamp%2C%20soft%20linen%20textures%20in%20background%2C%20cozy%20bedroom%20setting%2C%20warm%20amber%20ambient%20lighting%2C%20intimate%20editorial%20photography&width=800&height=800&seq=206&orientation=squarish',
  s8: 'https://readdy.ai/api/search-image?query=Fresh%20lifestyle%20product%20photography%20with%20product%20on%20a%20clean%20white%20kitchen%20counter%2C%20open%20wooden%20shelving%20behind%20with%20fresh%20herbs%2C%20natural%20daylight%2C%20modern%20kitchen%20setting%2C%20bright%20airy%20editorial%20photography&width=800&height=800&seq=207&orientation=squarish',
  s9: 'https://readdy.ai/api/search-image?query=Botanical%20lifestyle%20product%20photography%20with%20product%20on%20natural%20stone%20garden%20table%20surrounded%20by%20potted%20plants%20and%20flowers%2C%20dappled%20sunlight%2C%20lush%20green%20foliage%20background%2C%20peaceful%20outdoor%20editorial%20photography&width=800&height=800&seq=208&orientation=squarish',
  s10: 'https://readdy.ai/api/search-image?query=Organic%20lifestyle%20product%20photography%20with%20product%20on%20a%20moss-covered%20rock%20in%20a%20forest%20clearing%2C%20soft%20diffused%20light%20through%20trees%2C%20earthy%20natural%20woodland%20setting%2C%20serene%20wilderness%20editorial%20photography&width=800&height=800&seq=209&orientation=squarish',
  s11: 'https://readdy.ai/api/search-image?query=Dreamy%20lifestyle%20product%20photography%20with%20product%20on%20a%20weathered%20wooden%20stump%20in%20a%20golden%20sunset%20meadow%2C%20tall%20grass%20swaying%2C%20warm%20orange%20sky%20on%20horizon%2C%20serene%20outdoor%20editorial%20photography&width=800&height=800&seq=210&orientation=squarish',
  s12: 'https://readdy.ai/api/search-image?query=Exotic%20lifestyle%20product%20photography%20with%20product%20nestled%20among%20large%20tropical%20monstera%20leaves%2C%20lush%20green%20jungle%20foliage%2C%20natural%20filtered%20light%2C%20vibrant%20botanical%20editorial%20photography&width=800&height=800&seq=211&orientation=squarish',
  s13: 'https://readdy.ai/api/search-image?query=Moody%20lifestyle%20product%20photography%20with%20product%20on%20a%20wooden%20cafe%20table%20by%20a%20rain-streaked%20window%2C%20warm%20amber%20interior%20light%2C%20rainy%20urban%20street%20visible%20outside%2C%20intimate%20cafe%20editorial%20photography&width=800&height=800&seq=212&orientation=squarish',
  s14: 'https://readdy.ai/api/search-image?query=Street%20lifestyle%20product%20photography%20with%20product%20against%20a%20white%20ceramic%20subway%20tile%20wall%2C%20industrial%20urban%20setting%2C%20gritty%20city%20aesthetic%2C%20raw%20streetwear%20editorial%20photography&width=800&height=800&seq=213&orientation=squarish',
  s15: 'https://readdy.ai/api/search-image?query=Industrial%20lifestyle%20product%20photography%20with%20product%20on%20a%20reclaimed%20wood%20table%20against%20exposed%20brick%20wall%2C%20steel%20beams%20overhead%2C%20industrial%20loft%20warehouse%20setting%2C%20raw%20urban%20editorial%20photography&width=800&height=800&seq=214&orientation=squarish',
  s16: 'https://readdy.ai/api/search-image?query=Neon%20lifestyle%20product%20photography%20with%20product%20on%20wet%20pavement%20surface%20with%20pink%20and%20blue%20neon%20signs%20reflecting%2C%20urban%20night%20alley%2C%20vibrant%20nightlife%20energy%2C%20cyberpunk%20editorial%20photography&width=800&height=800&seq=215&orientation=squarish',
  s17: 'https://readdy.ai/api/search-image?query=Ultra%20minimal%20product%20photography%20with%20product%20on%20a%20pure%20white%20seamless%20surface%20with%20a%20single%20soft%20shadow%2C%20stark%20studio%20aesthetic%2C%20clean%20modern%20display%2C%20professional%20minimal%20editorial%20photography&width=800&height=800&seq=216&orientation=squarish',
  s18: 'https://readdy.ai/api/search-image?query=Brutalist%20product%20photography%20with%20product%20on%20a%20raw%20textured%20concrete%20block%20surface%2C%20industrial%20aesthetic%2C%20harsh%20directional%20shadow%2C%20architectural%20minimalism%2C%20stark%20modern%20editorial%20photography&width=800&height=800&seq=217&orientation=squarish',
  s19: 'https://readdy.ai/api/search-image?query=Artisan%20product%20photography%20with%20product%20on%20a%20sheet%20of%20crinkled%20brown%20craft%20paper%2C%20natural%20organic%20textures%2C%20soft%20shadows%2C%20handmade%20aesthetic%2C%20warm%20earthy%20tones%2C%20sustainable%20editorial%20photography&width=800&height=800&seq=218&orientation=squarish',
  s20: 'https://readdy.ai/api/search-image?query=Contemporary%20product%20photography%20with%20product%20on%20a%20transparent%20glass%20shelf%20surface%2C%20subtle%20reflections%2C%20clean%20modern%20aesthetic%2C%20sleek%20display%2C%20airy%20transparent%20editorial%20photography&width=800&height=800&seq=219&orientation=squarish',
  s21: 'https://readdy.ai/api/search-image?query=Opulent%20product%20photography%20with%20product%20on%20a%20deep%20emerald%20velvet%20fabric%20surface%20with%20gold%20trim%20details%2C%20rich%20luxurious%20texture%2C%20soft%20dramatic%20lighting%2C%20high-end%20editorial%20photography&width=800&height=800&seq=220&orientation=squarish',
  s22: 'https://readdy.ai/api/search-image?query=Glamorous%20product%20photography%20with%20product%20on%20an%20elegant%20crystal%20glass%20tray%2C%20sparkling%20light%20refractions%2C%20luxurious%20vanity%20setting%2C%20refined%20beauty%20editorial%20photography&width=800&height=800&seq=221&orientation=squarish',
  s23: 'https://readdy.ai/api/search-image?query=Cozy%20lifestyle%20product%20photography%20with%20product%20on%20a%20small%20wooden%20side%20table%20next%20to%20stacked%20books%2C%20warm%20knit%20blanket%20nearby%2C%20soft%20window%20light%2C%20reading%20nook%20atmosphere%2C%20hygge%20editorial%20photography&width=800&height=800&seq=222&orientation=squarish',
  s24: 'https://readdy.ai/api/search-image?query=Spa%20lifestyle%20product%20photography%20with%20product%20on%20the%20edge%20of%20a%20white%20marble%20bathtub%2C%20lit%20candles%20and%20eucalyptus%20nearby%2C%20soft%20warm%20spa%20lighting%2C%20tranquil%20bathroom%20retreat%2C%20serene%20self-care%20editorial%20photography&width=800&height=800&seq=223&orientation=squarish',
  s25: 'https://readdy.ai/api/search-image?query=Classical%20product%20photography%20with%20product%20on%20a%20warm%20cream%20marble%20ledge%20with%20classical%20stone%20architecture%20in%20background%2C%20elegant%20European%20aesthetic%2C%20soft%20natural%20light%2C%20timeless%20sophisticated%20editorial%20photography&width=800&height=800&seq=224&orientation=squarish',
  s26: 'https://readdy.ai/api/search-image?query=Romantic%20product%20photography%20with%20product%20nestled%20on%20flowing%20blush%20pink%20silk%20ribbon%20with%20gentle%20folds%2C%20delicate%20feminine%20aesthetic%2C%20soft%20dreamy%20atmosphere%2C%20elegant%20editorial%20photography&width=800&height=800&seq=225&orientation=squarish',
};

// Get result image for a scene (backward compat)
export function getResultImage(sceneId: string, offset = 0): string {
  const base = resultImages[sceneId] || resultImages.s1;
  if (offset === 0) return base;
  // For carousel variants, shift the seq number
  return base.replace(/seq=\d+/, `seq=${200 + offset}`);
}

// Get 4 carousel result images for a product x scene combo
export function getCarouselResultImages(sceneId: string): string[] {
  const baseSeq = {
    s1: 200, s2: 201, s3: 202, s4: 203, s5: 204, s6: 205,
    s7: 206, s8: 207, s9: 208, s10: 209, s11: 210, s12: 211,
    s13: 212, s14: 213, s15: 214, s16: 215, s17: 216, s18: 217,
    s19: 218, s20: 219, s21: 220, s22: 221, s23: 222, s24: 223,
    s25: 224, s26: 225,
  }[sceneId] || 200;

  return [0, 1, 2, 3].map((i) => {
    const base = resultImages[sceneId] || resultImages.s1;
    return base.replace(/seq=\d+/, `seq=${baseSeq + i * 10}`);
  });
}

// Demo products
export const demoProducts = [
  {
    id: 'p1',
    name: 'White Earbuds',
    image: 'https://readdy.ai/api/search-image?query=Simple%20plain%20white%20studio%20product%20photo%20of%20a%20sleek%20white%20wireless%20earbuds%20case%20on%20a%20pure%20white%20background%2C%20flat%20lighting%2C%20minimal%20shadows%2C%20amateur%20product%20photography%2C%20clean%20but%20basic%20composition%2C%20e-commerce%20style%20raw%20photo%20without%20styling%20or%20props&width=300&height=300&seq=80&orientation=squarish',
    category: 'Electronics',
  },
  {
    id: 'p2',
    name: 'Lavender Perfume',
    image: 'https://readdy.ai/api/search-image?query=Simple%20studio%20product%20photo%20of%20an%20elegant%20glass%20perfume%20bottle%20with%20lavender%20colored%20liquid%20on%20a%20plain%20white%20background%2C%20flat%20lighting%2C%20basic%20product%20photography%2C%20clean%20minimal%20composition%2C%20amateur%20e-commerce%20photo%20style&width=300&height=300&seq=81&orientation=squarish',
    category: 'Beauty',
  },
  {
    id: 'p3',
    name: 'Leather Wallet',
    image: 'https://readdy.ai/api/search-image?query=Simple%20studio%20product%20photo%20of%20a%20brown%20leather%20bifold%20wallet%20on%20a%20plain%20white%20background%2C%20flat%20even%20lighting%2C%20minimal%20shadows%2C%20basic%20e-commerce%20product%20photography%2C%20clean%20straightforward%20composition&width=300&height=300&seq=82&orientation=squarish',
    category: 'Accessories',
  },
  {
    id: 'p4',
    name: 'Ceramic Mug',
    image: 'https://readdy.ai/api/search-image?query=Simple%20studio%20product%20photo%20of%20a%20matte%20white%20ceramic%20coffee%20mug%20on%20a%20plain%20white%20background%2C%20flat%20lighting%2C%20minimal%20shadows%2C%20amateur%20e-commerce%20product%20photography%2C%20basic%20clean%20composition&width=300&height=300&seq=83&orientation=squarish',
    category: 'Home',
  },
  {
    id: 'p5',
    name: 'Sneakers',
    image: 'https://readdy.ai/api/search-image?query=Simple%20studio%20product%20photo%20of%20a%20pair%20of%20white%20minimalist%20sneakers%20on%20a%20plain%20white%20background%2C%20flat%20even%20lighting%2C%20minimal%20shadows%2C%20basic%20e-commerce%20product%20photography%2C%20clean%20straightforward%20composition&width=300&height=300&seq=84&orientation=squarish',
    category: 'Fashion',
  },
  {
    id: 'p6',
    name: 'Hand Cream',
    image: 'https://readdy.ai/api/search-image?query=Simple%20studio%20product%20photo%20of%20a%20small%20white%20tube%20of%20hand%20cream%20on%20a%20plain%20white%20background%2C%20flat%20lighting%2C%20minimal%20shadows%2C%20basic%20e-commerce%20product%20photography%2C%20clean%20minimal%20composition&width=300&height=300&seq=85&orientation=squarish',
    category: 'Beauty',
  },
];

// Per-product result mapping for demo (uses getResultImage)
export const demoResults: Record<string, Record<string, string>> = {};
demoProducts.forEach((p) => {
  demoResults[p.id] = {};
  demoScenes.forEach((s) => {
    demoResults[p.id][s.id] = getResultImage(s.id);
  });
});
